import type { DiscoverItem } from "../types";

/**
 * Day trips and multi-stop excursions. The category decides, by its slug —
 * the same string in every language, unlike the display name, which is how the
 * app's version of this once let six live rows walk past it in English and
 * every trip in the catalogue through in Arabic.
 */
const TRIP_CATEGORY_SLUGS = new Set([
  "trips",
  "day-trips",
  "city-tour",
  "excursions",
]);

/**
 * A multi-stop trip is a route, not a place. Pinning one to its departure city
 * tells the traveller something untrue: a fifteen-day tour that happens to
 * leave from Tangier is not a thing to do in Tangier.
 *
 * The browse drops these at the source rather than late in the pipeline, so the
 * map, the city counts, the tab counts and the list all agree about what is in
 * front of the traveller. It is the browse only — a day trip is still a real
 * row everywhere else, so it stays likeable, searchable from the header, and
 * addable as a stop in a trip.
 *
 * This lives here rather than in either surface because the phone and the
 * desktop have to answer the question the same way. They did not, briefly, and
 * the site quoted two different totals depending on the width of the window.
 */
export function isTrip(item: DiscoverItem): boolean {
  const slug = item.categorySlug;
  if (slug) return TRIP_CATEGORY_SLUGS.has(slug);
  // Explore rows carry no category at all, and a couple of real day trips are
  // filed that way, so the name is the only signal left. Guesswork — which is
  // exactly why it never applies to a row that has a category to check.
  return /day\s?trips?|excursion|\btrips?\b|circuit|\d+-day|\d+\s*jours?|tour\s+from/i.test(
    item.title ?? "",
  );
}
